import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { change } from "redux-form";
import { Icon } from "rhombus-react";

import { useTooltipRebuild } from "hooks/useTooltipRebuild";
import { PAC_FORM_TYPES, PAC_TYPES, PAC_TYPE_LABELS } from "./constants";
import { SOCIAL_WIZARD } from "widgets/forms/list";
import { PAC_FIELDS_BY_AD_TYPE } from "./constants/placements";
import { getFormStateByAdType } from "selectors/Social2";

import "./PACHeader.css";

export default function PACHeader(props){
	const dispatch = useDispatch();

	const {
		adType,
		pacType,
		isViewOnly,
		setActivePlacement,
	} = props;
	const { values } = useSelector(state => getFormStateByAdType(state, adType));
	const {
		placements = {},
		_currentActivePAC: currentPAC,
	} = values || {};

	const isDefault = (currentPAC === PAC_FORM_TYPES.DEFAULT);
	const activePlacements = Object.values(placements)
		.filter(item => item.status === "active")
		.sort((item1, item2) => item1.type.id - item2.type.id)
	;

	const currentIndex = activePlacements.findIndex(item => (item.type.name === currentPAC));
	const lastActivePlacementName = activePlacements[activePlacements.length - 1]?.type?.name;

	const hasPrev = !isDefault;
	const hasNext = activePlacements.length && (currentPAC !== lastActivePlacementName);
	const pacTypeLabel = PAC_TYPE_LABELS[pacType] || "";

	useTooltipRebuild();

	const summary = useSelector((state) => {
		if(isDefault){
			/*
				We receive a "Default" placement from the API, but we don't have it in the form state.
				This is just a special case that doesn't follow the `Name (Description)` format of
				other placements, anyway, so just hard code it.
			*/
			return "Default Assets";
		}

		let description = placements?.[currentPAC]?.type?.description;

		if(description){
			return `${currentPAC} (${description})`;
		}
		return currentPAC;
	});

	function goToPrev(){
		if(currentIndex === 0){
			dispatch(setActivePlacement(PAC_FORM_TYPES.DEFAULT));
		} else {
			let targetPlacementName = activePlacements[currentIndex - 1]?.type?.name;
			if(targetPlacementName){
				dispatch(setActivePlacement(targetPlacementName));
			}
		}
	}

	function goToNext(){
		if(currentIndex < (activePlacements.length - 1)){
			let targetPlacementName = activePlacements[currentIndex + 1]?.type?.name;
			if(targetPlacementName){
				dispatch(setActivePlacement(targetPlacementName));
			}
		}
	}

	function copyDefaultValues(){
		const fieldNames = PAC_FIELDS_BY_AD_TYPE[adType][currentPAC] || [];
		const fieldsWithNoDefaultValue = ["image", "video", "thumbnail"];

		fieldNames?.forEach((field) => {
			if(fieldsWithNoDefaultValue.includes(field)){
				return;
			}

			const defaultValue = values[field];
			if(defaultValue){
				/*
					This will only copy values if they exist on the default. It will not empty out
					a placement value if the default's value is blank.
				*/
				if(typeof(defaultValue) === "object" && defaultValue.hasOwnProperty("value")){
					// If the field is dynamic, we only want to copy the field value, and not isDynamic and dynVarName
					dispatch(change(SOCIAL_WIZARD, `${adType}.placements.${currentPAC}.${field}.value`, defaultValue.value));
				} else {
					dispatch(change(SOCIAL_WIZARD, `${adType}.placements.${currentPAC}.${field}`, defaultValue));
				}
			}
		});
	}

	return (
		<div className="pac-header">
			<div className="summary">
				{summary}
			</div>

			<div className="controls">
				{(!isDefault && !isViewOnly) && <a
					className = "copy-values"
					href = "#"
					onClick = {copyDefaultValues}
				>
					Copy Default Text Assets
				</a>}
				<div className="navigation">
					<Icon
						name = "left"
						iconStyle = "far"
						className = {classNames({ disabled: !hasPrev })}
						onClick = {goToPrev}
						data-tip = {`Previous ${pacTypeLabel}`}
					/>
					<Icon
						name="right"
						iconStyle="far"
						className={classNames({ disabled: !hasNext })}
						onClick = {goToNext}
						data-tip = {`Next ${pacTypeLabel}`}
					/>
				</div>
			</div>
		</div>
	);
}

PACHeader.displayName = "PACHeader";

PACHeader.propTypes = {
	adType: PropTypes.string.isRequired,
	pacType: PropTypes.oneOf(Object.values(PAC_TYPES)),
	setActivePlacement: PropTypes.func,
};
